import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit, Trash2, Search, QrCode, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

export default function MasterData() {
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [editSupplierDialogOpen, setEditSupplierDialogOpen] = useState(false);
  const [editItemDialogOpen, setEditItemDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [qrItem, setQrItem] = useState(null);
  const [importWarehouse, setImportWarehouse] = useState('');
  const [warehouses, setWarehouses] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const user = getUser();

  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const canEdit = userRoles.some(r => ['Admin', 'Purchasing'].includes(r));
  const canDelete = userRoles.includes('Admin');

  // Filtered data based on search
  const filteredSuppliers = suppliers.filter(s => 
    s.name.toLowerCase().includes(supplierSearch.toLowerCase()) ||
    (s.contact || '').toLowerCase().includes(supplierSearch.toLowerCase())
  );

  const filteredItems = items.filter(i => 
    i.name.toLowerCase().includes(itemSearch.toLowerCase()) ||
    (i.category || '').toLowerCase().includes(itemSearch.toLowerCase()) ||
    (i.description || '').toLowerCase().includes(itemSearch.toLowerCase())
  );

  const fetchData = async () => {
    try {
      const [suppliersRes, itemsRes, whRes] = await Promise.all([
        api.get('/suppliers'),
        api.get('/items'),
        api.get('/warehouses')
      ]);
      setSuppliers(suppliersRes.data);
      setItems(itemsRes.data);
      setWarehouses(whRes.data);
      if (whRes.data.length > 0 && !importWarehouse) setImportWarehouse(whRes.data[0].id);
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEditSupplier = (supplier) => {
    setSelectedSupplier(supplier);
    setEditSupplierDialogOpen(true);
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    setEditItemDialogOpen(true);
  };

  const handleDeleteSupplier = async (supplierId, supplierName) => {
    if (!window.confirm(`Are you sure you want to delete supplier "${supplierName}"?`)) return;
    
    try {
      await api.delete(`/suppliers/${supplierId}`);
      toast.success('Supplier deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete supplier');
    }
  };

  const handleDeleteItem = async (itemId, itemName) => {
    if (!window.confirm(`Are you sure you want to delete item "${itemName}"?`)) return;
    
    try {
      await api.delete(`/items/${itemId}`);
      toast.success('Item deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete item');
    }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="master-data-page">
      <div>
        <h1 className="text-4xl font-bold text-primary mb-2">Master Data</h1>
        <p className="text-muted-foreground">Manage suppliers and items</p>
      </div>

      <Tabs defaultValue="suppliers" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="suppliers">Suppliers</TabsTrigger>
          <TabsTrigger value="items">Items</TabsTrigger>
        </TabsList>

        <TabsContent value="suppliers" className="space-y-4">
          <div className="flex justify-between items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search suppliers..."
                value={supplierSearch}
                onChange={(e) => setSupplierSearch(e.target.value)}
                className="pl-10 bg-white border-2 border-primary/20"
              />
            </div>
            <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="add-supplier-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Supplier
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Supplier</DialogTitle>
                </DialogHeader>
                <SupplierForm 
                  onSuccess={() => {
                    setSupplierDialogOpen(false);
                    fetchData();
                  }} 
                />
              </DialogContent>
            </Dialog>
          </div>

          <Card className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="suppliers-table">
                <thead>
                  <tr className="border-b bg-secondary/50">
                    <th className="text-left p-3 font-semibold">Supplier Name</th>
                    <th className="text-left p-3 font-semibold">Contact</th>
                    <th className="text-left p-3 font-semibold">Address</th>
                    <th className="text-center p-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSuppliers.map((supplier) => (
                    <tr key={supplier.id} className="border-b hover:bg-accent/20 transition-colors">
                      <td className="p-3 font-medium">{supplier.name}</td>
                      <td className="p-3">{supplier.contact || '-'}</td>
                      <td className="p-3">{supplier.address || '-'}</td>
                      <td className="p-3">
                        <div className="flex items-center justify-center space-x-1">
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditSupplier(supplier)}
                              title="Edit Supplier"
                            >
                              <Edit className="h-4 w-4 text-primary" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteSupplier(supplier.id, supplier.name)}
                              title="Delete Supplier"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="items" className="space-y-4">
          <div className="flex justify-between items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search items by name, category..."
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                className="pl-10 bg-white border-2 border-primary/20"
              />
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex items-center gap-2">
                <select
                  value={importWarehouse}
                  onChange={(e) => setImportWarehouse(e.target.value)}
                  className="h-10 rounded-md border border-input bg-background px-2 py-1 text-sm"
                  data-testid="import-warehouse-select"
                >
                  <option value="">Tanpa stok</option>
                  {warehouses.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
                <label>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    data-testid="import-excel-input"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append('file', file);
                      let url = '/items/import';
                      if (importWarehouse) url += `?warehouse=${encodeURIComponent(importWarehouse)}`;
                      try {
                        const res = await api.post(url, formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        toast.success(res.data.message);
                        fetchData();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Import gagal');
                      }
                      e.target.value = '';
                    }}
                  />
                  <Button variant="outline" className="cursor-pointer" asChild data-testid="import-excel-btn">
                    <span><Upload className="h-4 w-4 mr-2" />Import Excel</span>
                  </Button>
                </label>
              </div>
              <Dialog open={itemDialogOpen} onOpenChange={setItemDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-item-button">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Item</DialogTitle>
                  </DialogHeader>
                  <ItemForm 
                    onSuccess={() => {
                      setItemDialogOpen(false);
                      fetchData();
                    }} 
                  />
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <Card className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="items-table">
                <thead>
                  <tr className="border-b bg-secondary/50">
                    <th className="text-left p-3 font-semibold">Item Name</th>
                    <th className="text-left p-3 font-semibold">Category</th>
                    <th className="text-left p-3 font-semibold">Description</th>
                    <th className="text-center p-3 font-semibold">Unit</th>
                    <th className="text-center p-3 font-semibold">Min Stock</th>
                    <th className="text-center p-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-accent/20 transition-colors">
                      <td className="p-3 font-medium">{item.name}</td>
                      <td className="p-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                          {item.category || 'Uncategorized'}
                        </span>
                      </td>
                      <td className="p-3">{item.description || '-'}</td>
                      <td className="p-3 text-center">
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-primary/10 text-primary">
                          {item.unit}
                        </span>
                      </td>
                      <td className="p-3 text-center font-medium">{item.minimum_stock || 0}</td>
                      <td className="p-3">
                        <div className="flex items-center justify-center space-x-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setQrItem(item)}
                            title="QR Code"
                            data-testid={`qr-btn-${item.id}`}
                          >
                            <QrCode className="h-4 w-4 text-blue-600" />
                          </Button>
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditItem(item)}
                              title="Edit Item"
                            >
                              <Edit className="h-4 w-4 text-primary" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteItem(item.id, item.name)}
                              title="Delete Item"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Supplier Dialog */}
      <Dialog open={editSupplierDialogOpen} onOpenChange={setEditSupplierDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Supplier</DialogTitle>
          </DialogHeader>
          {selectedSupplier && (
            <SupplierForm 
              editMode={true}
              existingSupplier={selectedSupplier}
              onSuccess={() => {
                setEditSupplierDialogOpen(false);
                setSelectedSupplier(null);
                fetchData();
              }} 
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog open={editItemDialogOpen} onOpenChange={setEditItemDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Item</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <ItemForm 
              editMode={true}
              existingItem={selectedItem}
              onSuccess={() => {
                setEditItemDialogOpen(false);
                setSelectedItem(null);
                fetchData();
              }} 
            />
          )}
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={!!qrItem} onOpenChange={(open) => !open && setQrItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>QR Code - {qrItem?.name}</DialogTitle>
          </DialogHeader>
          {qrItem && (
            <div className="flex flex-col items-center space-y-4" data-testid="qr-dialog">
              <div id="qr-print-area" className="bg-white p-6 rounded-lg border text-center">
                <QRCodeSVG value={qrItem.id} size={200} level="H" />
                <p className="mt-3 font-bold text-lg">{qrItem.name}</p>
                <p className="text-sm text-muted-foreground">{qrItem.category} &middot; {qrItem.unit}</p>
                <p className="text-xs text-muted-foreground mt-1">ID: {qrItem.id.slice(0, 8)}...</p>
              </div>
              <Button
                className="btn-primary w-full"
                data-testid="print-qr-btn"
                onClick={() => {
                  const printContent = document.getElementById('qr-print-area');
                  const win = window.open('', '_blank');
                  win.document.write(`<html><head><title>QR - ${qrItem.name}</title><style>body{display:flex;justify-content:center;align-items:center;min-height:100vh;margin:0;font-family:sans-serif}div{text-align:center}p{margin:4px 0}@media print{body{margin:0}}</style></head><body>`);
                  win.document.write(printContent.innerHTML);
                  win.document.write('</body></html>');
                  win.document.close();
                  win.print();
                }}
              >
                <QrCode className="w-4 h-4 mr-2" />
                Print QR Code
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SupplierForm({ onSuccess, editMode = false, existingSupplier = null }) {
  const [formData, setFormData] = useState(
    editMode && existingSupplier ? {
      name: existingSupplier.name,
      contact: existingSupplier.contact || '',
      address: existingSupplier.address || ''
    } : {
      name: '',
      contact: '',
      address: ''
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await api.put(`/suppliers/${existingSupplier.id}`, formData);
        toast.success('Supplier updated successfully');
      } else {
        await api.post('/suppliers', formData);
        toast.success('Supplier added successfully');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'add'} supplier`);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Supplier Name *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          placeholder="e.g., PT Sumber Jaya"
          className="bg-white border-2 border-primary/30 text-gray-900"
          style={{color: '#000000'}}
        />
      </div>

      <div>
        <Label>Contact Number</Label>
        <Input
          value={formData.contact}
          onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
          placeholder="e.g., 021-12345678"
          className="bg-white border-2 border-primary/30 text-gray-900"
          style={{color: '#000000'}}
        />
      </div>

      <div>
        <Label>Address</Label>
        <Input
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          placeholder="e.g., Jakarta"
          className="bg-white border-2 border-primary/30 text-gray-900"
          style={{color: '#000000'}}
        />
      </div>

      <Button type="submit" className="w-full btn-primary">
        {editMode ? 'Update Supplier' : 'Add Supplier'}
      </Button>
    </form>
  );
}

function ItemForm({ onSuccess, editMode = false, existingItem = null }) {
  const [formData, setFormData] = useState(
    editMode && existingItem ? {
      name: existingItem.name,
      description: existingItem.description || '',
      unit: existingItem.unit,
      category: existingItem.category || 'Uncategorized',
      minimum_stock: existingItem.minimum_stock || 0
    } : {
      name: '',
      description: '',
      unit: 'pcs',
      category: 'Uncategorized',
      minimum_stock: 0
    }
  );
  const [categories, setCategories] = useState([]);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const units = ['pcs', 'unit', 'box', 'karton', 'lusin', 'set', 'liter', 'kg', 'gram', 'meter', 'roll', 'btl'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await api.put(`/items/${existingItem.id}`, formData);
        toast.success('Item updated successfully');
      } else {
        await api.post('/items', formData);
        toast.success('Item added successfully');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editMode ? 'update' : 'add'} item`);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Item Name *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          placeholder="e.g., Handuk Mandi"
          className="bg-white border-2 border-primary/30 text-gray-900"
          style={{color: '#000000'}}
        />
      </div>

      <div>
        <Label>Description</Label>
        <Input
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="e.g., Handuk mandi ukuran besar"
          className="bg-white border-2 border-primary/30 text-gray-900"
          style={{color: '#000000'}}
        />
      </div>

      <div>
        <Label>Unit *</Label>
        <select
          value={formData.unit}
          onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
          className="flex h-10 w-full rounded-md border-2 border-primary/30 bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          style={{color: '#000000'}}
          required
        >
          {units.map(unit => (
            <option key={unit} value={unit}>{unit}</option>
          ))}
        </select>
      </div>

      <div>
        <Label>Category *</Label>
        <select
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          className="flex h-10 w-full rounded-md border-2 border-primary/30 bg-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          style={{color: '#000000'}}
          required
        >
          {categories.map(cat => (
            <option key={cat.id} value={cat.id} title={cat.description}>
              {cat.name}
            </option>
          ))}
        </select>
        <p className="text-xs text-muted-foreground mt-1">
          Kategori untuk grouping barang
        </p>
      </div>

      <div>
        <Label>Minimum Stock Level</Label>
        <Input
          type="number"
          value={formData.minimum_stock}
          onChange={(e) => setFormData({ ...formData, minimum_stock: e.target.value })}
          placeholder="e.g., 10"
          className="bg-white border-2 border-primary/30 text-gray-900"
          style={{color: '#000000'}}
          step="1"
          min="0"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Alert akan muncul jika stock dibawah jumlah ini
        </p>
      </div>

      <Button type="submit" className="w-full btn-primary">
        {editMode ? 'Update Item' : 'Add Item'}
      </Button>
    </form>
  );
}
